import { Session } from './types/session';
import api from '.';
import { AxiosResponse } from 'axios';
import { PaginatedResponse } from './types/pagination';
import { Transcript } from 'src/api/types/transcript';
import { SessionSummary } from './types/sessionSummary';

const fetchSessions = async (limit = 10, page = 1) => {
  return await api.get<
    {
      limit: number;
      page: number;
    },
    AxiosResponse<PaginatedResponse<Session>>
  >('/sessions', {
    params: { limit, page }
  });
};

const fetchSessionsByCampaignId = async (
  campaignId: string,
  limit = 10,
  page = 1,
  sortBy?: string
) => {
  const params: any = { limit, page };
  if (sortBy) {
    params.sortBy = sortBy;
  }

  return await api.get<
    {
      limit: number;
      page: number;
      sortBy?: string;
    },
    AxiosResponse<PaginatedResponse<Session>>
  >(`/campaigns/${campaignId}/sessions`, {
    params
  });
};

const fetchSessionsById = async (sessionId: string) => {
  return await api.get<null, AxiosResponse<Session>>(`/sessions/${sessionId}`);
};

const fetchTranscriptsBySessionId = async (sessionId: string, limit = 10, page = 1) => {
  return await api.get<
    {
      limit: number;
      page: number;
    },
    AxiosResponse<PaginatedResponse<Transcript>>
  >(`/sessions/${sessionId}/transcripts`, {
    params: { limit, page }
  });
};

const updateSession = async (sessionId: string, sessionData: Partial<Session>) => {
  return await api.patch<Partial<Session>, AxiosResponse<Session>>(
    `/sessions/${sessionId}`,
    sessionData
  );
};

const deleteSession = async (sessionId: string) => {
  return await api.delete<null, AxiosResponse<void>>(`/sessions/${sessionId}`);
};

const fetchCurrentSession = async () => {
  return await api.get<null, AxiosResponse<Session | null>>('/sessions/current');
};

const createSession = async (campaignId: string, sessionData: Partial<Session> = {}) => {
  return await api.post<typeof sessionData & { campaignId: string }, AxiosResponse<Session>>(
    '/sessions',
    { ...sessionData, campaignId }
  );
};

const startSession = async (sessionId: string) => {
  return await api.patch<{ startDate: Date }, AxiosResponse<Session>>(`/sessions/${sessionId}`, {
    startDate: new Date()
  });
};

const fetchSessionSummaries = async (sessionId: string) => {
  return await api.get<null, AxiosResponse<SessionSummary[]>>(`/sessions/${sessionId}/summaries`);
};

const fetchLatestSessionSummary = async (sessionId: string) => {
  return await api.get<null, AxiosResponse<SessionSummary>>(
    `/sessions/${sessionId}/summary/latest`
  );
};

const fetchLatestSessionRecap = async (sessionId: string) => {
  return await api.get<null, AxiosResponse<SessionSummary>>(`/sessions/${sessionId}/recap/latest`);
};

const updateSessionSummary = async (summaryId: string, summaryData: { summary: string }) => {
  return await api.patch<{ summary: string }, AxiosResponse<SessionSummary>>(
    `/sessions/summary/${summaryId}`,
    summaryData
  );
};

const regenerateSessionContent = async (
  sessionId: string,
  options: { regenerateSummary?: boolean; regenerateRecap?: boolean }
) => {
  return await api.post<
    { regenerateSummary?: boolean; regenerateRecap?: boolean },
    AxiosResponse<{
      message: string;
      queuedSummary: boolean;
      queuedRecap: boolean;
    }>
  >(`/sessions/${sessionId}/regenerate`, options);
};

const fetchKeyObjectsBySummaryId = async (summaryId: string) => {
  return await api.get<null, AxiosResponse<{ data: any[]; count: number }>>(
    `/key-objects/summary/${summaryId}`
  );
};

const SessionsApi = {
  fetchSessions,
  fetchSessionsByCampaignId,
  fetchSessionsById,
  fetchTranscriptsBySessionId,
  fetchCurrentSession,
  updateSession,
  deleteSession,
  createSession,
  startSession,
  fetchSessionSummaries,
  fetchLatestSessionSummary,
  fetchLatestSessionRecap,
  updateSessionSummary,
  regenerateSessionContent,
  fetchKeyObjectsBySummaryId
};

export default SessionsApi;
